import { AutoComplete } from "primereact/autocomplete";
import { useCallback, useEffect, useRef, useState } from "react";
import useAsyncEffect from "../../../hooks/useAsyncEffect";
import agent from "../../../agent";
import styles from "./style.module.scss";

const itemsToLoad = 10;

const VirtualScrollDropdown = ({
    code, searchCode, id, initialValue, initialLabel, onChange, placeholder, mode = "monitoring",
    labelField = "nameRu", setCatalogOptions = null, disabled = false
}) => {
    const formatString = (value) => {
        if (!value) {
            return "";
        }

        const stringValue = String(value);

        if (mode === "billing") {
            return stringValue;
        }

        switch (stringValue.length) {
            case 0:
                return stringValue;
            case 1:
                return stringValue.toUpperCase();
            default:
                const [ firstLetter, ...otherLetters ] = stringValue.split("");
                return firstLetter.toUpperCase() + otherLetters.join("").toLowerCase();
        }
    }

    const [query, setQuery] = useState("");
    const [value, setValue] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(false);

    const totalRef = useRef(0);
    const lastQueryRef = useRef("");
    const selectingRef = useRef(false);
    const loadedPagesByQuery = useRef(new Map());
    const isLastByQuery = useRef(new Map());
    const optionById = useRef(new Map());
    const containerRef = useRef(null);
    const autoCompleteRef = useRef(null);
    const panelMousedownHandlerRef = useRef(null);
    const hideBySelectRef = useRef(false);
    const horizontalScrollActiveRef = useRef(false);
    const idKey = code
        .split("-")
        .map((w, i) => (i ? w[0].toUpperCase() + w.slice(1) : w)).join("")
        + "Id";

    const getOptionLabel = (option) => {
        if (!option) {
            return "";
        }

        if (option.__label) {
            return option.__label;
        }

        const raw = option[labelField] ?? option.nameRu ?? "";
        return formatString(raw);
    };

    const renderOption = useCallback((option) => {
        if (!option) {
            return null;
        }

        if (typeof option !== "object") {
            return String(option);
        }

        return getOptionLabel(option);
    }, []);

    useAsyncEffect(async () => {
        await fetchPage(0, query || "");
    }, [query, searchCode, mode]);

    useEffect(() => {
        if (!initialValue && !initialLabel) {
            setValue(null);
            setQuery("");
            return;
        }

        if (initialValue && typeof initialValue === "object") {
            const seeded = {
                ...initialValue,
                __label: getOptionLabel(initialValue),
            };

            if (seeded.id != null) {
                optionById.current.set(seeded.id, seeded);
            }

            setValue(seeded);
            setQuery(seeded.__label);
            return;
        }

        if (typeof initialValue === "string") {
            const label = formatString(initialValue);
            const seeded = { __label: label };

            setValue(seeded);
            setQuery(label);
            return;
        }

        if (initialLabel) {
            const label = formatString(initialLabel);
            const seeded = { __label: label };

            setValue(seeded);
            setQuery(label);
        }
    }, [initialValue, initialLabel, labelField]);

    useEffect(() => {
        if (setCatalogOptions) {
            setCatalogOptions(suggestions);
        }
    }, [suggestions, setCatalogOptions]);

    useEffect(() => {
        if (!autoCompleteRef.current) {
            return;
        }

        const input = autoCompleteRef.current.getInput();

        const onScroll = () => {
            if (input.scrollWidth > input.clientWidth) {
                horizontalScrollActiveRef.current = true;
                horizontalScrollActiveRef.current = false;
            }
        };

        input.addEventListener("scroll", onScroll, { passive: true });
    }, []);

    const getLoadedPagesSet = (query) => {
        const key = query || "";

        if (!loadedPagesByQuery.current.has(key)) {
            loadedPagesByQuery.current.set(key, new Set());
        }

        return loadedPagesByQuery.current.get(key);
    };

    const resetPagingFor = (query) => {
        totalRef.current = 0;
        setSuggestions([]);

        loadedPagesByQuery.current.set(query ?? "", new Set());
        isLastByQuery.current.set(query ?? "", false);
    };

    const writePage = (page, rows) => {
        const normalized = rows.map(row => {
            if (!row) {
                return row;
            }

            return {
                ...row,
                __label: formatString(row[labelField] ?? row.nameRu ?? ""),
            };
        });

        setSuggestions((prev) => {
            const next = prev.length ? [...prev] : [];
            const start = page * itemsToLoad;

            if (start <= next.length) {
                next.splice(start, normalized.length, ...normalized);
            } else {
                next.push(...normalized);
            }

            normalized.forEach((r) => r?.id && optionById.current.set(r.id, r));

            return next;
        });
    };

    const fetchPage = async (page, query) => {
        const loaded = getLoadedPagesSet(query);

        if (loaded.has(page)) {
            return;
        }

        let res;

        if (mode === "billing") {
            if (searchCode === "activity") {
                res = {
                    totalElements: 1,
                    last: true,
                    content: [
                        {
                            nameRu: "Активен"
                        }
                    ]
                }
            } else {
                res = await agent.AccountingDictionary.listRecords(searchCode, page, itemsToLoad);
            }
        } else {
            res = await agent.CatalogType.searchCatalogTypes(searchCode, query, page, itemsToLoad);
        }

        if (!totalRef.current) {
            totalRef.current = res.totalElements;

            setSuggestions([]);
            lastQueryRef.current = query ?? "";
        }

        isLastByQuery.current.set(query || "", !!res?.last);

        writePage(page, res.content || []);
        loaded.add(page);
    };

    const onLazyLoad = useCallback(async (e) => {
        if (loading) {
            return;
        }

        setLoading(true);

        const trimmedQuery = query.trim();
        const firstPage = Math.floor(e.first / itemsToLoad);
        const lastIndex = Math.max(e.last - 1, e.first);
        const lastPage = Math.floor(lastIndex / itemsToLoad);

        const tasks = [];

        for (let p = firstPage; p <= lastPage; p++) {
            const loaded = getLoadedPagesSet(trimmedQuery);

            if (!loaded.has(p)) {
                tasks.push(fetchPage(p, trimmedQuery));
            }
        }

        const atEnd = lastIndex >= (suggestions.length - 1);
        const isLast = !!isLastByQuery.current.get(trimmedQuery);

        if (atEnd && !isLast) {
            const nextPage = Math.max(-1, ...Array.from(getLoadedPagesSet(trimmedQuery))) + 1;
            const loaded = getLoadedPagesSet(trimmedQuery);

            if (!loaded.has(nextPage)) {
                tasks.push(fetchPage(nextPage, trimmedQuery));
            }
        }

        await Promise.all(tasks);
        setLoading(false);
    }, [loading, query, itemsToLoad]);

    const syncPanelWidth = () => {
        const currentRef = autoCompleteRef.current;

        if (!currentRef) {
            return;
        }

        const root = currentRef.getElement();
        const panel = currentRef.getOverlay();

        if (root && panel) {
            panel.style.width = `${root.offsetWidth}px`;
        }
    };

    const attachPanelMousedownGuard = () => {
        const currentRef = autoCompleteRef.current;

        if (!currentRef) {
            return;
        }

        const panel = currentRef.getOverlay();

        if (panelMousedownHandlerRef.current) {
            panel.removeEventListener("mousedown", panelMousedownHandlerRef.current);
        }

        panelMousedownHandlerRef.current = (e) => {
            selectingRef.current = true;
            e.stopPropagation();
        };
        panel.addEventListener("mousedown", panelMousedownHandlerRef.current);
    };

    const detachPanelMousedownGuard = () => {
        const currentRef = autoCompleteRef.current;

        if (!currentRef) {
            return;
        }

        const panel = currentRef.getOverlay();

        if (panel && panelMousedownHandlerRef.current) {
            panel.removeEventListener("mousedown", panelMousedownHandlerRef.current);
        }

        panelMousedownHandlerRef.current = null;
        selectingRef.current = false;
    };

    const handleHide = useCallback(() => {
        detachPanelMousedownGuard();

        if (hideBySelectRef.current) {
            hideBySelectRef.current = false;
            return;
        }

        if (horizontalScrollActiveRef.current) {
            requestAnimationFrame(() => {
                autoCompleteRef.current.show()
            });
            return;
        }

        requestAnimationFrame(blurAutocomplete);
    }, [
        hideBySelectRef.current,
        horizontalScrollActiveRef.current,
        autoCompleteRef.current
    ]);

    const schedulePanelPreparation = () => {
        requestAnimationFrame(() => {
            syncPanelWidth();
            attachPanelMousedownGuard();
        });
    };

    const completeMethod = useCallback(async (e) => {
        const term = (e.query || "").trim();

        setQuery(term);
        resetPagingFor(term);

        await fetchPage(0, term);

        const ref = autoCompleteRef.current;

        if (!ref) {
            return;
        }

        ref.show();
        schedulePanelPreparation();
    }, [autoCompleteRef.current]);

    const emitSelection = (option) => {
        const newId = option ? option.id : null;

        switch(mode) {
            case "monitoring":
                onChange(id, idKey, newId);
                break;
            case "order":
                onChange(idKey, newId);
                break;
            case "billing":
                onChange(option || null);
                break;
            default:
                console.error("Unknown mode: ", mode);
        }
    };

    const handleChange = useCallback((e) => {
        const inputValue = e.value;

        if (typeof inputValue === "string") {
            setQuery(inputValue);
            setValue(inputValue);
        }
        else if (inputValue) {
            setValue(inputValue);
            setQuery(getOptionLabel(inputValue));
        } else {
            setQuery("");
            setValue(null);
        }
    }, []);

    const handleSelect = useCallback((e) => {
        const inputValue = e.value || null;

        setValue(inputValue);
        setQuery(getOptionLabel(inputValue));
        emitSelection(inputValue);

        selectingRef.current = false;
        hideBySelectRef.current = true;

        requestAnimationFrame(() => {
            requestAnimationFrame(blurAutocomplete);
        });
    }, [selectingRef.current, hideBySelectRef.current]);

    const handleClear = useCallback(async () => {
        if (selectingRef.current) {
            return;
        }

        setValue(null);
        setQuery("");

        resetPagingFor("");
        emitSelection(null);

        await fetchPage(0, "");
    }, [selectingRef.current]);

    const handleShow = useCallback(async () => {
        const currentQuery = (query || getOptionLabel(value)).trim();

        await completeMethod({ query: currentQuery });
        schedulePanelPreparation();
    }, [query, value]);

    const stopRowSelect = (e) => {
        e.stopPropagation();
    };

    const blurAutocomplete = () => {
        const currentAutocompleteRef = autoCompleteRef.current;
        const currentContainerRef = containerRef.current;

        if (!(currentAutocompleteRef || currentContainerRef)) {
            return;
        }

        const input = currentAutocompleteRef.getInput();
        const root = currentAutocompleteRef.getElement();

        input.blur();
        root.querySelector("button.p-autocomplete-dropdown").blur();

        const active = document.activeElement;

        if (active && root && root.contains(active)) {
            active.blur();
        }

        containerRef.current.focus();
        containerRef.current.blur();
    };

    return (
        <div className={styles[`${mode === "order" ? "order" : "general"}Container`]}
             ref={containerRef}
             onClick={stopRowSelect}
             onMouseDown={stopRowSelect}
        >
            <AutoComplete className={styles.dropdown}
                          ref={autoCompleteRef}
                          dropdown
                          dropdownMode="current"
                          minLength={0}
                          value={value}
                          suggestions={suggestions}
                          disabled={disabled}
                          field="__label"
                          itemTemplate={renderOption}
                          selectedItemTemplate={renderOption}
                          placeholder={placeholder}
                          showEmptyMessage
                          emptyMessage="Нет результатов"
                          completeMethod={completeMethod}
                          onChange={handleChange}
                          onSelect={handleSelect}
                          onClear={handleClear}
                          onShow={handleShow}
                          onHide={handleHide}
                          virtualScrollerOptions={{
                              lazy: true,
                              onLazyLoad,
                              itemSize: 38,
                              showLoader: true,
                              loading
                          }}
                          pt={{
                              input: { root: { className: styles.dropdownInput } },
                              dropdownButton: { root: { className: styles.dropdownButton } },
                              panel: { className: styles.dropdownPanel }
                          }}
            />
        </div>
    );
};

export default VirtualScrollDropdown;